import {Base64} from '@colibrio/colibrio-reader-framework/colibrio-core-base';
import {BlobUrlManager} from '@colibrio/colibrio-reader-framework/colibrio-core-io-base';
import {ILocator} from '@colibrio/colibrio-reader-framework/colibrio-core-locator';
import {
    IPublicationMetadataItem,
    PublicationType,
} from '@colibrio/colibrio-reader-framework/colibrio-core-publication-base';
import {
    IContentLocation, IPublicationStyleFontFace,
    IReaderDocument,
    IReaderPublication,
    IReaderPublicationStorageState, IReaderViewOptions,
    NavigationCollectionType,
} from '@colibrio/colibrio-reader-framework/colibrio-readingsystem-base';
import {
    IEpubReaderPublicationOptions,
} from '@colibrio/colibrio-reader-framework/colibrio-readingsystem-formatadapter-epub';
import {
    IPdfReaderPublicationOptions,
} from '@colibrio/colibrio-reader-framework/colibrio-readingsystem-formatadapter-pdf';
import {
    IWpAudiobookReaderPublicationOptions,
} from '@colibrio/colibrio-reader-framework/colibrio-readingsystem-formatadapter-wp-audiobook';

import {VanillaReaderEventBus} from '../VanillaReaderEventBus/VanillaReaderEventBus';
import {
    IVanillaReaderBookmarkData,
    IVanillaReaderContentPositionData,
    IVanillaReaderHighlightData,
    IVanillaReaderPublicationCacheData,
    IVanillaReaderPublicationData,
    IVanillaReaderPublicationOptionsData, IVanillaReaderPublicationStyleOptions,
    IVanillaReaderReadingProgressionTimeline,
    IVanillaReaderSyntheticPageListData,
    IVanillaReaderTtsOptions,
    VanillaReaderPublicationLandmarkType,
} from './VanillaReaderModel';
import {VanillaReaderNavigationTree} from './VanillaReaderNavigationTree';
import {VanillaReaderPublicationLandmarkCollection} from './VanillaReaderPublicationLandmarkCollection';
import {VanillaReaderUtils} from './VanillaReaderUtils';
import {VanillaReaderView} from './VanillaReaderView';
import {VanillaReaderShelfItem} from '../VanillaReaderBookshelf/VanillaReaderShelfItem';
import {VanillaReaderPublicationPageList} from './VanillaReaderPublicationPageList';
import {IWpPublication} from "@colibrio/colibrio-reader-framework/colibrio-core-publication-wp";
import {VanillaReaderAppAssets} from "../VanillaReaderApp/VanillaReaderAppAssets";
import {ObjectUtils} from "../utils/base/ObjectUtils/ObjectUtils";

/*
* # VanillaReaderPublication
*
* ## PRIMARY COLIBRIO FRAMEWORK TYPES
*
* - IReaderPublication
*
* ## RESPONSIBILITIES
*
* This is a helper wrapper around the Colibrio Reader Framework's `IReaderPublication` type. It's main purpose
* is to abstract away the Colibrio "base type", and also to add a more rich serializable format that holds publication
* specific runtime data such as the latest reading position, highlights and bookmarks etc.
*
* */

export class VanillaReaderPublication {

    private readonly _id: string = '';
    private readonly _fileName: string = '';
    private readonly _fileSourceUri: string;
    private readonly _title: string | undefined = '';
    private readonly _format: PublicationType;
    private readonly _url: string = '';
    private readonly _isRtl: boolean = false;
    private readonly _isFixedLayout: boolean = false;
    private readonly _hasMoSyncMedia: boolean = false;
    private readonly _metadata: IPublicationMetadataItem[] = [];
    private readonly _coverImageUrl: URL | undefined = undefined;
    private _coverImageData: string | undefined = undefined;
    private _blobUrlManager: BlobUrlManager = new BlobUrlManager();
    private _landmarksCollection: VanillaReaderPublicationLandmarkCollection | undefined = undefined;
    //private _landmarksCollectionData: IVanillaReaderPublicationLandmarkData | undefined = undefined;
    private readonly _navigationTree: VanillaReaderNavigationTree;
    private _progressionTimeline: IVanillaReaderReadingProgressionTimeline | undefined;
    private _currentReadingPositionData: IVanillaReaderContentPositionData | undefined;
    private _ttsOptions: IVanillaReaderTtsOptions | undefined;
    private _pageList: VanillaReaderPublicationPageList | undefined;
    private _publicationStyleOptions: IVanillaReaderPublicationStyleOptions | undefined;

    private constructor(
        fileName: string,
        fileSourceUri: string,
        private _colibrioReaderPublication: IReaderPublication,
        private _shelfItem: VanillaReaderShelfItem,
    ) {

        // An EPUB can contain both fixed layout documents and reflowable document. In the Vanilla Reader however we treat a publication that has
        // one or more "pre-paginated" content documents as being fixed layout.
        const _publicationIsFixedLayout = this._colibrioReaderPublication.getSpine().some((doc: IReaderDocument) => {
            return doc.getSourceContentDocument().getLayout() === 'PRE_PAGINATED';
        });

        this._fileName = fileName;
        this._fileSourceUri = fileSourceUri;
        this._id = this._colibrioReaderPublication.getSourcePublication().getMetadata().getIdentifiers()[0]?.content.value || fileSourceUri;
        this._title = this._colibrioReaderPublication.getSourcePublication().getMetadata().getTitles()[0]?.content.value;
        this._url = this._colibrioReaderPublication.getDefaultLocatorUrl();
        this._format = this._colibrioReaderPublication.getSourcePublication().getType();
        this._isFixedLayout = _publicationIsFixedLayout;
        this._isRtl = this._colibrioReaderPublication.getSourcePublication().getPageProgressionDirection() === 'RTL';
        this._hasMoSyncMedia = this._colibrioReaderPublication.getAvailableSyncMediaFormats().some(value => value === 'EPUB_MEDIA_OVERLAY');
        this._metadata = this._colibrioReaderPublication.getSourcePublication().getMetadata().getAll();

        // Cover images are only available for EPUBs and Audiobooks at the moment.
        if (VanillaReaderUtils.isEpubReaderPublication(this._colibrioReaderPublication) || VanillaReaderUtils.isWpAudiobookReaderPublication(this._colibrioReaderPublication)) {
            this._coverImageUrl = this._colibrioReaderPublication.getSourcePublication().getCoverImageResourceUrl() || undefined;
        }

        // The navigation tree is instantiated, but it is not yet built. That happens when the timeline has been built.
        // This is because we want to map timeline positions to navigation items.
        // See `VanillaReader._event_timelineReady()` and `VanillaReaderNavigationTree.build()`
        this._navigationTree = new VanillaReaderNavigationTree();
    }

    static async create(
        fileName: string,
        fileSourceUri: string,
        colibrioReaderPublication: IReaderPublication,
        shelfItem: VanillaReaderShelfItem,
    ): Promise<VanillaReaderPublication> {
        const publication = new VanillaReaderPublication(fileName, fileSourceUri, colibrioReaderPublication, shelfItem);
        await publication.initializeState().catch(console.warn);
        return publication;
    }

    private async initializeState(): Promise<void> {
        let publicationHasStoredCachedData = await this._shelfItem.hasCachedData();
        let publicationHasStoredOptionsData = await this._shelfItem.hasOptionsData();

        if (publicationHasStoredCachedData) {
            let initialPublicationCacheDataState = await this._shelfItem.fetchCacheData();
            // Updating the tree data seems to be an issue as it can create duplicated. Not sure why still.
            // if (this._initialPublicationCacheDataState?.navigationTreeData) {
            //     this._navigationTree.setTreeData(this._initialPublicationCacheDataState?.navigationTreeData);
            // }

            if (initialPublicationCacheDataState?.landmarkData) {
                // The actual landmarksCollection is created late, so we need to store the data in order to
                // set it later in the `fetchLandmarksCollection()` method.
                //this._landmarksCollectionData = initialPublicationCacheDataState.landmarkData;
            }
        }

        if (publicationHasStoredOptionsData) {
            let initialPublicationOptionsDataState = await this._shelfItem.fetchOptionsData();

            if (initialPublicationOptionsDataState?.ttsOptions) {
                this._ttsOptions = initialPublicationOptionsDataState.ttsOptions;
            }
            if(initialPublicationOptionsDataState?.styleOptions) {
                this.publicationStyleOptions = initialPublicationOptionsDataState.styleOptions;
            }
        }

        // If the shelf item has not been persisted before we update the VanillaReaderShelfItem to include the metadata
        // from the actual publication. Publication data is not going to change over time.
        if (!this._shelfItem.hasBeenPersisted) {
            await this._shelfItem.setPublicationData(await this.serialize());
        }

        if (!publicationHasStoredOptionsData) {
            this._persistCurrentPublicationOptionsState();
        }

        if (!publicationHasStoredCachedData) {
            this._persistCurrentPublicationCacheDataState();
        }
    }

    public get id(): string {
        return this._id;
    }

    public get fileName(): string {
        return this._fileName;
    }

    public get fileSourceUri(): string {
        return this._fileSourceUri;
    }

    public get title(): string | undefined {
        return this._title;
    }

    public get url(): string {
        return this._url;
    }

    public get format(): string {
        return this._format;
    }

    public get isRtl(): boolean {
        return this._isRtl;
    }

    public get isFixedLayout(): boolean {
        return this._isFixedLayout;
    }

    public get isAudiobook(): boolean {
        return this._format === PublicationType.WP;
    }

    public get hasMoSyncMedia(): boolean {
        return this._hasMoSyncMedia;
    }

    public get metadata(): IPublicationMetadataItem[] {
        return this._metadata;
    }

    public get language(): string | undefined {
        if (this._colibrioReaderPublication.getSourcePublication().getMetadata().getLanguages().length > 0) {
            return this._colibrioReaderPublication.getSourcePublication().getMetadata().getLanguages()[0].content.value || undefined;
        } else {
            return;
        }
    }

    public get navigationTree(): VanillaReaderNavigationTree {
        return this._navigationTree;
    }

    public get currentReadingPositionData(): IVanillaReaderContentPositionData | undefined {
        return this._currentReadingPositionData;
    }

    public setCurrentReadingPositionData(readingPositionData: IVanillaReaderContentPositionData | undefined): void {
        this._currentReadingPositionData = readingPositionData;
        if (this._shelfItem && readingPositionData && readingPositionData.locator) {
            // Save the new reading position data to the `IVanillaReaderPublicationDataStore` store.
            this._shelfItem.setReadingPositionData(readingPositionData).catch(console.warn);
        }
    }

    public get storageState(): IReaderPublicationStorageState {
        return this._colibrioReaderPublication.getReaderPublicationStorage().getStorageState();
    }

    public set storageState(state: IReaderPublicationStorageState) {
        this._colibrioReaderPublication.getReaderPublicationStorage().setStorageState(state);
    }

    public get ttsOptions(): IVanillaReaderTtsOptions | undefined {
        return this._ttsOptions;
    }

    public set ttsOptions(options: IVanillaReaderTtsOptions | undefined) {
        this._ttsOptions = options;

        // Save the new TTS options data to the `IVanillaReaderPublicationDataStore` store.
        this._shelfItem?.updateOptionsData({
            publicationId: this.id,
            ttsOptions: options,

        }).catch(console.warn);

    }

    public get colibrioReaderPublication(): IReaderPublication {
        return this._colibrioReaderPublication;
    }

    public set progressionTimeline(instance: IVanillaReaderReadingProgressionTimeline | undefined) {
        this._progressionTimeline = instance;

        // Save the new timeline data to the `IVanillaReaderPublicationDataStore` store.
        this._shelfItem?.updateCacheData({
            publicationId: this.id,
            timelineData: this._progressionTimeline?.toSerializedData(),
        }).catch(console.warn);

    }

    public get progressionTimeline(): IVanillaReaderReadingProgressionTimeline | undefined {
        return this._progressionTimeline;
    }

    public get pageListData(): IVanillaReaderSyntheticPageListData | undefined {
        return this._pageList?.serialize();
    }

    public get pageList(): VanillaReaderPublicationPageList | undefined {
        return this._pageList;
    }

    public get publicationStyleOptions(): IVanillaReaderPublicationStyleOptions | undefined {
        return this._publicationStyleOptions;
    }

    public set publicationStyleOptions(styleOptions: IVanillaReaderPublicationStyleOptions | undefined) {
        if(ObjectUtils.deepEqual(styleOptions, this._publicationStyleOptions)){
            console.log('VanillaReaderPublication.publicationStyleOptions, IVanillaReaderPublicationStyleOptions options are identical.');
            return;
        }

        this._publicationStyleOptions = styleOptions;
        VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('PUBLICATION_STYLE_OPTIONS_CHANGED', {styleOptions: styleOptions ?? {}}));
    }

    async fetchLandmarkCollection(timeline?: IVanillaReaderReadingProgressionTimeline, landmarkTypesToInclude?: VanillaReaderPublicationLandmarkType[]): Promise<VanillaReaderPublicationLandmarkCollection | undefined> {
        if (this._landmarksCollection) {
            return this._landmarksCollection;
        }

        this._landmarksCollection = new VanillaReaderPublicationLandmarkCollection(this, timeline);

        // We have data that we can restore so that we do not need to repopulate the collection.
        // if (this._landmarksCollectionData) {
        //     this._landmarksCollection.setPublicatonLandmarkData(this._landmarksCollectionData);
        //     return this._landmarksCollection;
        // }

        landmarkTypesToInclude = (landmarkTypesToInclude && landmarkTypesToInclude.length > 0) ? landmarkTypesToInclude : [
            VanillaReaderPublicationLandmarkType.FIGURE,
            VanillaReaderPublicationLandmarkType.IMAGE,
            // VanillaReaderPublicationLandmarkType.ORDERED_LIST,
            // VanillaReaderPublicationLandmarkType.UNORDERED_LIST,
            // VanillaReaderPublicationLandmarkType.DESCRIPTION_LIST,
            VanillaReaderPublicationLandmarkType.TABLE,
            VanillaReaderPublicationLandmarkType.HEADING1,
            VanillaReaderPublicationLandmarkType.HEADING2,
            VanillaReaderPublicationLandmarkType.HEADING3,
            VanillaReaderPublicationLandmarkType.HEADING4,
            VanillaReaderPublicationLandmarkType.HEADING5,
            VanillaReaderPublicationLandmarkType.HEADING6,
            VanillaReaderPublicationLandmarkType.FOOTNOTE,
        ];

        await this._landmarksCollection.populate(undefined, landmarkTypesToInclude);

        // Save the landmark data to the `IVanillaReaderPublicationDataStore` store.
        // this._shelfItem?.updateCacheData({
        //     publicationId: this.id,
        //     landmarkData: this._landmarksCollection.getAll(),
        // }).catch(console.warn);

        return this._landmarksCollection;
    }

    async fetchLandmarkCollectionForDocument(
        documentIndex: number,
        vanillaReadingProgressionTimeline?: IVanillaReaderReadingProgressionTimeline,
        landmarkTypesToInclude?: VanillaReaderPublicationLandmarkType[],
    ): Promise<VanillaReaderPublicationLandmarkCollection | undefined> {

        let landmarksCollection = new VanillaReaderPublicationLandmarkCollection(this, vanillaReadingProgressionTimeline);

        if (!landmarkTypesToInclude) {
            landmarkTypesToInclude = [
                VanillaReaderPublicationLandmarkType.FIGURE,
                VanillaReaderPublicationLandmarkType.IMAGE,
                VanillaReaderPublicationLandmarkType.ORDERED_LIST,
                VanillaReaderPublicationLandmarkType.UNORDERED_LIST,
                VanillaReaderPublicationLandmarkType.DESCRIPTION_LIST,
                VanillaReaderPublicationLandmarkType.TABLE,
                VanillaReaderPublicationLandmarkType.HEADING1,
                VanillaReaderPublicationLandmarkType.HEADING2,
                VanillaReaderPublicationLandmarkType.HEADING3,
                VanillaReaderPublicationLandmarkType.HEADING4,
                VanillaReaderPublicationLandmarkType.HEADING5,
                VanillaReaderPublicationLandmarkType.HEADING6,
                VanillaReaderPublicationLandmarkType.FOOTNOTE,
            ];
        }

        await landmarksCollection.populate([documentIndex], landmarkTypesToInclude);
        return landmarksCollection;
    }

    async fetchCoverImageAsBase64(): Promise<string | undefined> {

        if (this._coverImageData) {
            return this._coverImageData;
        }

        if (VanillaReaderUtils.isEpubReaderPublication(this._colibrioReaderPublication) && this._coverImageUrl) {
            let imageResource = await this._colibrioReaderPublication.getSourcePublication().getEpubResourceProvider().fetch(this._coverImageUrl);
            if (imageResource) {
                this._coverImageData = Base64.encode(await imageResource.asUint8Array());
                return this._coverImageData;

            }
        } else if (VanillaReaderUtils.isWpAudiobookReaderPublication(this._colibrioReaderPublication)) {
            const publication = this._colibrioReaderPublication.getSourcePublication() as IWpPublication;
            const coverImageResourceUrl = publication.getCoverImageResourceUrl();
            if (coverImageResourceUrl) {
                const coverImageResponse = await publication.getBackingResourceProvider().fetch(coverImageResourceUrl);
                this._coverImageData = Base64.encode(await coverImageResponse.asUint8Array());
                return this._coverImageData;
            }
        }

        return undefined;
    }

    async fetchResourceBlobUrl(url: string, forceDataUrl: boolean = false): Promise<string | undefined> {
        let resource;

        if (VanillaReaderUtils.isEpubReaderPublication(this._colibrioReaderPublication)) {
            resource = await this._colibrioReaderPublication.getSourcePublication().getEpubResourceProvider().fetch(url);
        }

        if (resource) {
            let resourceData = await resource.asUint8Array();
            let mediaType = resource.getMetadata().mediaType;
            if (mediaType) {
                return this._blobUrlManager.createUrl([resourceData], mediaType, forceDataUrl).url;
            }
        }
        return undefined;
    }

    revokeResourceBlobUrl(blobUrl: string): void {
        this._blobUrlManager.revokeUrl(blobUrl);
    }

    revokeAllResourceBlobUrls(): void {
        this._blobUrlManager.revokeAllUrls();
    }

    getColibrioReaderPublication(): IReaderPublication {
        return this._colibrioReaderPublication;
    }

    /**
     *
     * This method takes in `IPublicationStyleOptions` data and applies the style options using `IReaderView.setOptions()`.
     * `IPublicationStyleOptions` include options for which fonts to use, what colors to display etc.
     *
     * */
    setPublicationStyleOptions(styleOptions: IVanillaReaderPublicationStyleOptions, vanillaReaderView: VanillaReaderView): void {

        let colibrioReaderViewOptions: IReaderViewOptions = {};
        colibrioReaderViewOptions.publicationStyleOptions = {
            defaultFontFamily: styleOptions.defaultFontFamily,
            fontSizeScaleFactor: styleOptions.fontSizeScaleFactor,
            lineHeightScaleFactor: styleOptions.lineHeightScaleFactor,
            textAlignment: styleOptions.textAlignment || null,
            pageMargins: styleOptions.pageMargins,
            palette: styleOptions.palette || null,
        }

        if(styleOptions.fontSet) {
            let fontFaces: IPublicationStyleFontFace[] = [];

            for(let fontFace of styleOptions.fontSet.fontFaces) {
                let fontSetAsset = VanillaReaderAppAssets.FontSets.find(fontSet => fontSet.fontFaces[0]?.family === fontFace.family);
                fontFaces.push({
                    family: fontFace.family,
                    fontWeight: fontFace.fontWeight,
                    fontStyle: fontFace.fontStyle,
                    fontStretch: fontFace.fontStretch,
                    mediaType: fontFace.mediaType,
                    src: fontSetAsset?.fontFaces[0]?.src || '',
                    srcUrl: fontSetAsset?.fontFaces[0]?.srcUrl || '',
                });
            }

            colibrioReaderViewOptions.publicationStyleOptions.fontSet = {
                defaults: styleOptions.fontSet.defaults,
                selectors: styleOptions.fontSet.selectors,
                fontFaces
            }
        } else {
            colibrioReaderViewOptions.publicationStyleOptions.fontSet = null;
        }

        // In the Colibrio Reader Framework, the ReaderView handles all rendering aspects (style options etc.), including
        // the current ReaderPublications. So we defer the actual applying of options to the VanillaReaderView component.
        vanillaReaderView.setVanillaReaderViewOptions({colibrioReaderViewOptions});

        if (!this.isAudiobook && styleOptions?.palette) {
            vanillaReaderView.setActivePalette(styleOptions.palette);
        }

        // Save the new option's data to the `IVanillaReaderPublicationDataStore` store.
        this._shelfItem?.updateOptionsData({
            publicationId: this.id,
            styleOptions: styleOptions,
        }).catch(console.warn);

        this.publicationStyleOptions = styleOptions;
    }

    setPublicationOptions(colibrioEpubPublicationOptionData: IEpubReaderPublicationOptions | IPdfReaderPublicationOptions | IWpAudiobookReaderPublicationOptions): void {

        if (VanillaReaderUtils.isEpubReaderPublication(this._colibrioReaderPublication)) {
            this._colibrioReaderPublication.setOptions(colibrioEpubPublicationOptionData);
        } else if (VanillaReaderUtils.isPdfReaderPublication(this.colibrioReaderPublication)) {
            this._colibrioReaderPublication.setOptions(colibrioEpubPublicationOptionData);
        } else if (VanillaReaderUtils.isWpAudiobookReaderPublication(this.colibrioReaderPublication)) {
            this._colibrioReaderPublication.setOptions(colibrioEpubPublicationOptionData);
        }

        let updatedOptions = this._colibrioReaderPublication.getOptions();

        VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('PUBLICATION_OPTIONS_CHANGED', {options: updatedOptions}));
    }

    async fetchPublicationOptionsData(): Promise<IVanillaReaderPublicationOptionsData | undefined> {
        return this._shelfItem?.fetchOptionsData();
    }

    async fetchHighlights(): Promise<IVanillaReaderHighlightData[]> {
        return this._shelfItem?.fetchHighlights() || [];
    }

    async addHighlight(highlightData: IVanillaReaderHighlightData): Promise<IVanillaReaderHighlightData | void> {
        let locator = highlightData.locator;
        let contentLocation = await this._colibrioReaderPublication.fetchContentLocation(highlightData.locator);

        highlightData.publicationId = this.id;

        let navItemRefs = await contentLocation?.fetchNavigationItemReferences({
            collectionTypes: [NavigationCollectionType.TOC],
            greedy: true,
        });

        if (navItemRefs) {
            // Get the last reference toc item text for the `bookSection` field.
            highlightData.bookSection = navItemRefs.getItemsInRange().length > 0 ?
                navItemRefs.getItemsInRange()[navItemRefs.getItemsInRange().length - 1].getNavigationItem().getTextContent() :
                undefined;
        }

        if (locator) {
            return this._shelfItem?.addHighlight(highlightData);
        } else {
            return;
        }
    }

    async updateHighlight(highlightData: IVanillaReaderHighlightData): Promise<void> {
        return this._shelfItem?.updateHighlight(highlightData);
    }

    async deleteHighlight(locator: string): Promise<void> {
        return this._shelfItem?.deleteHighlight(locator);
    }

    async fetchBookmarks(): Promise<IVanillaReaderBookmarkData[]> {
        return this._shelfItem?.fetchBookmarks() || [];
    }

    async addBookmark(locator: ILocator | string): Promise<IVanillaReaderBookmarkData | undefined> {
        let location: IContentLocation | undefined | null;
        let bookmarkData: IVanillaReaderBookmarkData | undefined = undefined;

        if (typeof locator === 'string') {
            locator = (await this._colibrioReaderPublication.fetchContentLocation(locator)).getLocator();
        }

        if (this.isAudiobook && this._progressionTimeline) {
            let timelinePosition = await this._progressionTimeline?.fetchTimelinePosition(locator);

            if (timelinePosition === undefined) {
                console.warn(`VanillaPublication.addBookmark(): Unable to get timeline position for locator ${locator.toString()}`);
                return;
            }

            location = await this._progressionTimeline.fetchContentLocation(timelinePosition);

            if (location) {
                let fetchResult = await location.fetchNavigationItemReferences({
                    collectionTypes: [NavigationCollectionType.TOC],
                    greedy: false,
                });
                let itemsInRange = fetchResult.getItemsInRange();
                let bookSection: string | undefined;
                if (itemsInRange && itemsInRange.length > 0) {
                    bookSection = itemsInRange[0].getNavigationItem().getTextContent();
                }

                bookmarkData = {
                    publicationId: this.id,
                    locator: location.getLocator().toString(),
                    bookSection,
                };
            }

        } else {
            location = await this._colibrioReaderPublication.fetchContentLocation(locator);
            let locationContent = await location?.fetchContentResolver();
            let textExtract = locationContent?.getTextContentAfter(250) || '';

            if (location) {
                let fetchResult = await location.fetchNavigationItemReferences({
                    collectionTypes: [NavigationCollectionType.TOC],
                    greedy: true,
                });

                let itemsInRange = fetchResult.getItemsInRange();
                let bookSection: string | undefined;
                if (itemsInRange && itemsInRange.length > 0) {
                    bookSection = itemsInRange[0].getNavigationItem().getTextContent();
                }

                bookmarkData = {
                    publicationId: this.id,
                    locator: location.getLocator().toString(),
                    bookSection,
                    text: textExtract,
                };
            }
        }

        if (bookmarkData) {
            this._shelfItem?.addBookmark(bookmarkData).catch(console.warn);
        }

        return bookmarkData;
    }

    async deleteBookmark(locator: string): Promise<void> {
        return this._shelfItem?.deleteBookmark(locator);
    }

    async serialize(): Promise<IVanillaReaderPublicationData> {
        return {
            id: this.id,
            fileName: this.fileName,
            fileSourceUri: this.fileSourceUri,
            isFixedLayout: this.isFixedLayout,
            url: this.url,
            title: this.title,
            isRtl: this.isRtl,
            hasMoSyncMedia: this.hasMoSyncMedia,
            isAudiobook: this.isAudiobook,
            format: this.format,
            metadata: this._colibrioReaderPublication.getSourcePublication().getMetadata().toJSON(),
            language: this.language,
            coverImageAsBase64: this._coverImageData || await this.fetchCoverImageAsBase64(),
        };
    }

    async buildNavigationTree(
        timeline?: IVanillaReaderReadingProgressionTimeline,
    ): Promise<void> {

        if (!this._navigationTree.hasBeenBuilt) {
            let navigation = await this._colibrioReaderPublication.fetchPublicationNavigation();

            await this._navigationTree.build(navigation, timeline);

            this._shelfItem?.updateCacheData({
                publicationId: this.id,
                navigationTreeData: this._navigationTree.getTreeData(),
            }).catch(console.warn);
        }

        VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('NAVIGATION_TREE_READY', {navigationTreeData: this.navigationTree?.getTreeData()}));
    }

    async buildPageList(
        timeline: IVanillaReaderReadingProgressionTimeline,
    ): Promise<void> {

        if(this.isAudiobook) {
            console.log('Not building pagelist since this is an audiobook.');
            return;
        }

        if (!this._navigationTree.hasBeenBuilt) {
            await this.buildNavigationTree();
        }

        this._pageList = new VanillaReaderPublicationPageList(timeline, this.navigationTree);
        let pageListData = await this._pageList.build();

        VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('PUBLICATION_PAGE_LIST_READY', {pageListData}));

    }

    async deleteStoredResources(): Promise<void> {
        return this._shelfItem?.deleteStoredResource();
    }

    /**
     *
     * PRIVATE METHODS
     *
     * */

    private _persistCurrentPublicationOptionsState(): void {
        this._shelfItem?.updateOptionsData({
            publicationId: this.id,
            ttsOptions: this._ttsOptions,
        }).catch(console.warn);
    }

    private _persistCurrentPublicationCacheDataState(): void {

        let publicationCacheData: IVanillaReaderPublicationCacheData = {
            publicationId: this.id,
            storageState: this.storageState,
            navigationTreeData: this.navigationTree?.getTreeData(),
            timelineData: this.progressionTimeline?.toSerializedData(),
        };

        // if (this._landmarksCollection || this._landmarksCollectionData) {
        //     publicationCacheData.landmarkData = this._landmarksCollectionData ?
        //         this._landmarksCollectionData :
        //         this._landmarksCollection?.getAll();
        // }

        this._shelfItem?.setCacheData(publicationCacheData).catch(console.warn);
    }

}
